import IMetric from './interfaces/IMeasuredMetric'
import {
  ans,
  attentionInhibitionWeight,
  cardSort,
  corsi,
  corsiRev,
  enumerationWeight,
  hyperactiveWeight,
  inhibitionWeight,
  mentalRotation,
  nonSymbolicWeight,
  planningWeight,
  sart,
  sartRev,
  shiftingWeight,
  subtizing,
  sustainAttentionWeight,
  symbolic,
  symbolicWeight,
  workingMemoryWeight
} from './normativeInfo'

export const NonSymbolicNumericalRepresentation: IMetric = {
  label: 'Non-Symbolic Numerical Representation',
  storeKey: 'Non-Symbolic Numerical Representation',
  href: 'https://en.wikipedia.org/wiki/Approximate_number_system',
  description:
    'The ability to estimate amounts of elements as accurately as possible without relying on language or symbols.',
  normData: ans,
  weight: nonSymbolicWeight
}

export const SymbolicNumericalRepresentation: IMetric = {
  label: 'Symbolic Numerical Representation',
  storeKey: 'Symbolic Numerical Representation',
  href: 'https://onlinelibrary.wiley.com/doi/full/10.1111/desc.12395',
  description:
    'The ability to quickly and accurately decide which of two displayed digits is greater, or generally, to estimate the magnitude of whole numbers (visual representation).',
  normData: symbolic,
  weight: symbolicWeight
}

export const Enumeration: IMetric = {
  label: 'Enumeration',
  storeKey: 'Enumeration',
  href: 'https://en.wikipedia.org/wiki/Subitizing',
  description: `The ability to report a number of displayed elements. \n Subitizing is used to quickly and accurately recognize a small group of elements without counting. When more elements are displayed, the task requires people to count or estimate. Counting is a slow and accurate process of reporting a large number of elements, while estimating is a fast and inaccurate process used when reporting a large number of elements with limited time.
    `,
  normData: subtizing,
  weight: enumerationWeight
}

export const ContinuousPerformanceAttention: IMetric = {
  label: 'Sustained Attention',
  storeKey: 'Continuous Performance Attention',
  href:
    'https://en.wikipedia.org/wiki/Continuous_performance_task#:~:targetText=A%20continuous%20performance%20task%2C%20continuous,and%20is%20associated%20with%20impulsivity.',
  description:
    'Sustained attention is the ability to maintain concentration over time, doing boring or unchallenging tasks. Selective attention is the ability to prioritize a particular stimulus and ignore competing stimuli. For example, the ability to maintain concentration throughout a lesson from start to finish.',
  normData: sart,
  weight: sustainAttentionWeight
}

export const AttentionInhibition: IMetric = {
  label: 'Inhibition',
  storeKey: 'Inhibition',
  href:
    'https://study.com/academy/lesson/cognitive-inhibition-definition-example.html',
  description: `The ability to tune out distractions, both environmental and internal, in order to focus on completing a task. This is also associated with the ability to delay gratification.
    For example, being able to ignore loud noises coming from the other room (environmental distractions) or having a craving for a snack (internal distraction) in order to stay focused on doing homework.`,
  normData: sartRev,
  weight: attentionInhibitionWeight
}

export const Inhibition: IMetric = {
  label: 'Inhibition',
  storeKey: 'Inhibition',
  href:
    'https://study.com/academy/lesson/cognitive-inhibition-definition-example.html',
  description: `The ability to tune out distractions, both environmental and internal, in order to focus on completing a task. This is also associated with the ability to delay gratification.
    For example, being able to ignore loud noises coming from the other room (environmental distractions) or having a craving for a snack (internal distraction) in order to stay focused on doing homework.`,
  normData: sartRev,
  weight: inhibitionWeight
}

export const Planning: IMetric = {
  label: 'Planning',
  storeKey: 'Planning',
  href: 'https://en.wikipedia.org/wiki/Planning_(cognitive)',
  description:
    'The ability to develop strategies and prioritize actions to accomplish goals.',
  normData: cardSort,
  weight: planningWeight
}

export const SpatialAbilities: IMetric = {
  label: 'Spatial Abilities',
  storeKey: 'Spatial Abilities',
  href: 'https://en.wikipedia.org/wiki/Spatial_ability',
  description:
    'Visual-spatial abilities are used for everyday use from navigation, understanding or fixing equipment, understanding or estimating distance and measurement.',
  normData: mentalRotation
}

export const FineMotor: IMetric = {
  label: 'Fine Motor',
  storeKey: 'Fine Motor',
  href: 'https://en.wikipedia.org/wiki/Fine_motor_skill',
  description:
    'The coordination of small muscles, in movements usually involving the synchronization of hands and fingers with the eyes. Some examples of tasks involving fine motor skills are writing neatly, buttoning clothing, turning pages, and cutting with scissors.'
}

export const GrossMotor: IMetric = {
  label: 'Gross Motor',
  storeKey: 'Gross Motor',
  href: 'https://en.wikipedia.org/wiki/Gross_motor_skill',
  description: `Involve the movement and coordination of the arms, legs, and other large body parts.
    Some examples of tasks involving gross motor skills are jumping, maintaining balance on one foot, and throwing or kicking a ball with accuracy.
    `
}

export const Social: IMetric = {
  label: 'Social',
  storeKey: 'Social',
  href: 'https://en.wikipedia.org/wiki/Self-efficacy',
  description:
    'An individual’s confidence in her/his ability to engage in social interactional tasks necessary to initiate and maintain interpersonal relationships.'
}

export const Emotional: IMetric = {
  label: 'Emotional',
  storeKey: 'Emotional',
  href: 'https://en.wikipedia.org/wiki/Self-efficacy',
  description:
    'An individual’s confidence in her/his capability to cope with negative emotions.'
}

export const Prosocial: IMetric = {
  label: 'Prosocial',
  storeKey: 'Prosocial',
  href: 'https://en.wikipedia.org/wiki/Prosocial_behavior',
  description:
    'Social behavior that benefits other people or society as a whole, such as helping, sharing, donating, co-operating, and volunteering. Obeying the rules and conforming to socially accepted behaviors are also regarded as prosocial behaviors.'
}

export const Hyperactivity: IMetric = {
  label: 'Hyperactivity',
  storeKey: 'Hyperactivity',
  href:
    'https://www.understood.org/en/learning-thinking-differences/child-learning-disabilities/hyperactivity-impulsivity/understanding-your-childs-trouble-with-hyperactivity',
  description: `Constantly being active in ways that aren’t appropriate for the time or setting.
    For example, difficulty staying in one place or waiting for a question to end before giving an answer.`,
  weight: hyperactiveWeight
}

export const EmotionalSymptoms: IMetric = {
  label: 'Emotional Symptoms',
  storeKey: 'Emotional Symptoms',
  href:
    'https://www.healthgrades.com/right-care/mental-health-and-behavior/emotional-symptoms',
  description:
    'Proneness to anxiety, sadness, fear, or insecurity. The cause for these symptoms can be both internal and environmental.'
}

export const ConductProblems: IMetric = {
  label: 'Conduct Problems',
  storeKey: 'Conduct Problems',
  href:
    'https://www.healthline.com/health/parenting/antisocial-behavior-in-children',
  description:
    'Behavior in which the basic rights of others or major age-appropriate norms are violated. These behaviors are often referred to as "antisocial behaviors".'
}

export const PeerProblems: IMetric = {
  label: 'Peer Problems',
  storeKey: 'Peer Problems',
  href:
    'https://www.sciencedirect.com/science/article/pii/B9780123430144500162',
  description:
    'Difficulty in making social connections with other children of the same age. Examples of peer relationship problems are bullying and being victimized by others.'
}

export const EarlyPhonologicalAwareness: IMetric = {
  label: 'Early Phonological Awareness',
  storeKey: 'Early Phonological Awareness',
  href:
    'https://www.understood.org/en/learning-thinking-differences/child-learning-disabilities/reading-issues/phonological-awareness-what-it-is-and-how-it-works',
  description:
    'The ability to recognize and work with sounds in spoken language, like being able to pick out rhyming words and count the number of syllables in a name.'
}

export const Vocabulary: IMetric = {
  label: 'Vocabulary',
  storeKey: 'Vocabulary',
  href: 'https://en.wikipedia.org/wiki/Vocabulary',
  description:
    "Set of familiar words within a person's language. A vocabulary, usually developed with age, serves as a useful and fundamental tool for communication and acquiring knowledge."
}

export const VisualSpatialWorkingMemory: IMetric = {
  label: 'Visual Spatial Working Memory',
  storeKey: 'Visual Spatial Working Memory',
  href: 'https://en.wikipedia.org/wiki/Spatial_memory',
  description:
    'The ability to record, recall, and manipulate information about different locations and spatial relations between objects. For example, this allows one to navigate around a familiar city.',
  normData: corsiRev
}

export const VisualSpatialShortTermMemory: IMetric = {
  label: 'Visual Spatial Short Term Memory',
  storeKey: 'Visual Spatial Short Term Memory',
  href:
    'https://en.wikipedia.org/wiki/Spatial_memory#Short-term_spatial_memory',
  description:
    'The ability to recall different locations as well as spatial relations between objects. For example, this allows one to remember where an object is in relation to other objects.',
  normData: corsi
}

export const Shifting: IMetric = {
  label: 'Shifting',
  storeKey: 'Planning',
  href: 'https://en.wikipedia.org/wiki/Planning_(cognitive)',
  description:
    'The ability to consciously redirect attention from one task to another.',
  normData: cardSort,
  weight: shiftingWeight
}

export const WorkingMemory: IMetric = {
  label: 'Working Memory',
  storeKey: 'Visual Spatial Working Memory',
  href: 'https://en.wikipedia.org/wiki/Spatial_memory#Spatial_working_memory',
  description:
    'Allows one to temporarily store and process information. This temporary store enables one to work on complex tasks while being able to keep information in mind.',
  normData: corsiRev,
  weight: workingMemoryWeight
}

export const Listening: IMetric = {
  label: 'Listening',
  storeKey: 'Listening',
  href: '#',
  description: 'The ability to hear and understand what others say.'
}

export const Speaking: IMetric = {
  label: 'Speaking',
  storeKey: 'Speaking',
  href: '#',
  description: 'The ability to express thoughts, ideas, and feelings clearly.'
}

export const Reading: IMetric = {
  label: 'Reading',
  storeKey: 'Reading',
  href: '#',
  description:
    'The ability to recognize and read words accurately and smoothly and understand the meaning of written text.'
}

export const Writing: IMetric = {
  label: 'Writing',
  storeKey: 'Writing',
  href: '#',
  description:
    'The ability to communicate using letters, words, and sentences and organize sentences into larger paragraphs to tell stories, present information, etc.'
}

export const SocialEmotional: IMetric = {
  label: 'Social/Emotional',
  storeKey: 'social_emotional',
  href: '#',
  description:
    'The ability to use language appropriately depending on the situation including greetings, maintaining conversation, taking turns in conversation, recognizing and expressing emotions, and understanding and using nonverbal communication such as eye contact.'
}

export default {
  NonSymbolicNumericalRepresentation,
  SymbolicNumericalRepresentation,
  Enumeration,
  ContinuousPerformanceAttention,
  AttentionInhibition,
  Inhibition,
  Planning,
  SpatialAbilities,
  FineMotor,
  GrossMotor,
  Social,
  Emotional,
  Prosocial,
  Hyperactivity,
  EmotionalSymptoms,
  ConductProblems,
  PeerProblems,
  EarlyPhonologicalAwareness,
  Vocabulary,
  VisualSpatialWorkingMemory,
  VisualSpatialShortTermMemory,
  Shifting,
  WorkingMemory,
  Listening,
  Speaking,
  Reading,
  Writing,
  SocialEmotional
}



// WEBPACK FOOTER //
// ./src/data/abitilies/metrics.ts